import type { WorkflowPimProduct } from '@/lib/sales-workflow/workflowPimProduct'
import type { StandardCustomFieldDraft } from '@/lib/sales-workflow/standardCustomFields'
import { normalizeStandardCustomFields } from '@/lib/sales-workflow/standardCustomFields'
import type { NewCustomerOrderWizardSnapshot } from '@/types/newCustomerOrderForm'
import type { OriginSubmitPayload, OriginSopWarning, OriginSpec } from '@/types/origin'

export type SalesWorkflowDraftTile =
  | 'modify_product'
  | 'discontinue_replace'
  | 'new_product'
  | 'new_product_bundle'
  | 'new_order'

/** Draft persistence tiles — bundle drafts land in SB-OUT-001. */
export type SalesWorkflowPersistedDraftTile = Exclude<
  SalesWorkflowDraftTile,
  'new_product_bundle'
>

export type ModifyChangeMode = 'modify' | 'discontinue' | null

export interface ModifyAttachment {
  name: string
  type: string
  size: number
  dataUrl: string
}

export interface ModifyReferenceSku {
  sku: string
  label: string
  /** ERP product uuid when selected from live PIM picker; absent on legacy mock-catalog drafts */
  id?: string
  thumbnail_url?: string | null
}

export interface ModifyWhiteInk {
  flood: boolean
  spot: boolean
  fiveWS: boolean
  threeWS: boolean
}

export interface ModifyFormData {
  why: string
  new_price: string
  effective_date: string
  uin_action: 'add' | 'replace' | 'remove'
  new_uin: string
  web_action: 'update' | 'add' | 'remove'
  web_title: string
  web_description: string
  web_thumbnail: string
  web_notes: string
  web_remove_reason: string
  spec_new_substrate: string
  spec_new_width: string
  spec_new_height: string
  spec_new_finishing: string
  design_description: string
  sb_substrate: string
  sb_width: string
  sb_height: string
  sb_single_double: string
  sb_graphic_method: string
  sb_vinyl_type: string
  sb_rta_color: string
  sb_white_ink: ModifyWhiteInk
  sb_reference_sku_enabled: boolean
  sb_reference_sku: ModifyReferenceSku | null
  sb_design_notes: string
  sb_laminate: string
  sb_taping: string
  sb_rounded_corners: string
  sb_rounded_corners_custom: string
  sb_mounting_holes: boolean
  sb_hole_location: string
  sb_hole_diameter: string
  sb_hardware_needed: boolean
  sb_hardware_desc: string
  sb_grommets: string
  sb_hem: string
  sb_notes: string
  /** Standard-Custom authoring — defaults disabled; does not seed from catalog (Origin parity). */
  std_custom_enabled: boolean
  std_custom_fields: StandardCustomFieldDraft[]
  discontinue_reason: string
  discontinue_effective: string
  /**
   * F-099 / F-050 (slice 7, Candace #12 + owner 2026-10-07): the parts of each selected product
   * (key = product id, else SKU) — Part 1 pre-filled from the PIM record, the rest added by
   * Sales. Everything optional; only the products Sales touched are sent (`parts_touched`).
   */
  parts_by_product: Record<string, ModifyPartDraft[]>
  parts_touched: Record<string, boolean>
  /** F-099 slice 7 — "Artwork Update" change type (Candace #12: colors, logos, fonts, layout). */
  artwork_colors: boolean
  artwork_logos: boolean
  artwork_fonts: boolean
  artwork_layout: boolean
  artwork_notes: string
  /**
   * F-131 / F-132 (Bridgette, owner 2026-10-08): a multi-product request may need different
   * changes per product. Every product after the first carries "Same changes as the first
   * product" (on by default); unticked (`product_linked[key] === false`) the product edits its
   * own copy of the whole change set (`product_overrides[key]`, key = product id, else SKU).
   * The first product and every linked product use the fields above.
   */
  product_linked: Record<string, boolean>
  product_overrides: Record<string, ModifyProductChanges>
}

/**
 * A product's own change set — the same shape as the form (so every field editor and builder
 * takes it as is); its `why`, `product_linked` and `product_overrides` are unused (kept empty).
 */
export type ModifyProductChanges = ModifyFormData

/** One modifier row on a Modify-form part — the Triage editor's inputs, Sales-optional (Amanda). */
export interface ModifyPartModifierDraft {
  code: string
  qty: string
  spacing_in: string
  size: string
  size_text: string
  bends: string
}

/** Sentinel substrate value: keep the stock PIM lists today (`current_substrate`). */
export const MODIFY_PART_CURRENT_SUBSTRATE = '__current__'

/** One part of a product on the Modify form (Part 1 comes from PIM; see pimPartDraft.ts). */
export interface ModifyPartDraft extends SignBuilderExtraSectionDraft {
  /**
   * The sourcing preset PIM gave Part 1 (manufactured_in_house / hardware_in_house /
   * hardware_drop_shipped / vended_drop_shipped) — kept under the vended mark so a hardware
   * product stays hardware in Triage; '' on parts Sales adds (the mark decides).
   */
  sourcing: string
  /** PIM's current stock text — shown as the "keep current" option when it is not a catalog pick. */
  current_substrate: string
  modifiers: ModifyPartModifierDraft[]
}

export function createEmptyModifyPartModifierDraft(code = ''): ModifyPartModifierDraft {
  return { code, qty: '', spacing_in: '', size: '', size_text: '', bends: '' }
}

export function createEmptyModifyPartDraft(
  overrides: Partial<ModifyPartDraft> = {}
): ModifyPartDraft {
  return {
    ...createEmptyExtraSectionDraft(),
    sourcing: '',
    current_substrate: '',
    modifiers: [],
    ...overrides,
  }
}

function normalizeModifyPartModifiers(raw: unknown): ModifyPartModifierDraft[] {
  if (!Array.isArray(raw)) return []
  return raw.map((entry) => {
    const m =
      entry && typeof entry === 'object' ? (entry as Partial<ModifyPartModifierDraft>) : {}
    const text = (v: unknown): string => (typeof v === 'string' ? v : '')
    return {
      code: text(m.code),
      qty: text(m.qty),
      spacing_in: text(m.spacing_in),
      size: text(m.size),
      size_text: text(m.size_text),
      bends: text(m.bends),
    }
  })
}

export function normalizeModifyPartsByProduct(raw: unknown): Record<string, ModifyPartDraft[]> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, ModifyPartDraft[]> = {}
  for (const [key, list] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue
    out[key] = normalizeExtraSections(list).map((section, i) => {
      const source = list[i] && typeof list[i] === 'object' ? (list[i] as Record<string, unknown>) : {}
      return {
        ...section,
        sourcing: typeof source.sourcing === 'string' ? source.sourcing : '',
        current_substrate:
          typeof source.current_substrate === 'string' ? source.current_substrate : '',
        modifiers: normalizeModifyPartModifiers(source.modifiers),
      }
    })
  }
  return out
}

function normalizeTouched(raw: unknown): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, boolean> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === true) out[key] = true
  }
  return out
}

export interface ModifyWizardSnapshot {
  step: 1 | 2 | 3
  selectedProducts: WorkflowPimProduct[]
  formData: ModifyFormData
  expanded: string[]
  changeMode: ModifyChangeMode
}

/** Persisted Sign Builder wizard state — attachments are session-only, never stored. */
export interface SignBuilderWizardSnapshot {
  formData: SignBuilderFormData
  openSections: number[]
  activeSection: number
  visitedSections: number[]
  /** @deprecated Replaced by `step` (3 = Review). Kept for old-draft hydration. */
  showSummary: boolean
  decisionAnswers: Record<number, number>
  /** Cart lines persisted without attachments (optional for pre-Phase-5 drafts). */
  builtProducts?: SignBuilderPersistedCartEntry[]
  editingProductIdx?: number | null
  /** Wizard step 1–3 (Request Info / Products / Review). Absent on pre-3-step drafts. */
  step?: number
  /** National-account client (Request Info). Absent on pre-3-step drafts. */
  client?: string
  /** Overall project description (UI-only — not on wire). Absent on pre-3-step drafts. */
  projectDescription?: string
}

export type SalesWorkflowDraftSnapshot =
  | ModifyWizardSnapshot
  | SignBuilderWizardSnapshot
  | NewCustomerOrderWizardSnapshot

export type ModifySalesRequestDraft = {
  id: string
  tile: 'modify_product' | 'discontinue_replace'
  summary: string
  snapshot: ModifyWizardSnapshot
  updatedAt: string
}

export type NewProductSalesRequestDraft = {
  id: string
  tile: 'new_product'
  summary: string
  snapshot: SignBuilderWizardSnapshot
  updatedAt: string
}

export type NewCustomerOrderSalesRequestDraft = {
  id: string
  tile: 'new_order'
  summary: string
  snapshot: NewCustomerOrderWizardSnapshot
  updatedAt: string
}

export type SalesRequestDraft =
  | ModifySalesRequestDraft
  | NewProductSalesRequestDraft
  | NewCustomerOrderSalesRequestDraft

export interface SalesSubmitResult {
  ref: string
  payload: OriginSubmitPayload
  tile: SalesWorkflowDraftTile
  uuid?: string
  hubspotTicketId?: string | null
}

export interface SalesSubmissionRecord {
  id: string
  ref: string
  tile: SalesWorkflowDraftTile
  payload: OriginSubmitPayload
  savedAt: number
  status?: string
  hubspotTicketId?: string | null
  hubspotTicketUrl?: string | null
  /** HS-TICKET-TIMING-002 — NEW Product D&D outbox queued. */
  hubspotTicketPending?: boolean
  summary?: string
}

export function createDefaultModifyFormData(): ModifyFormData {
  return {
    why: '',
    new_price: '',
    effective_date: '',
    uin_action: 'add',
    new_uin: '',
    web_action: 'update',
    web_title: '',
    web_description: '',
    web_thumbnail: '',
    web_notes: '',
    web_remove_reason: '',
    spec_new_substrate: '',
    spec_new_width: '',
    spec_new_height: '',
    spec_new_finishing: '',
    design_description: '',
    sb_substrate: '',
    sb_width: '',
    sb_height: '',
    sb_single_double: 'single',
    sb_graphic_method: '',
    sb_vinyl_type: '',
    sb_rta_color: '',
    sb_white_ink: { flood: false, spot: false, fiveWS: false, threeWS: false },
    sb_reference_sku_enabled: false,
    sb_reference_sku: null,
    sb_design_notes: '',
    sb_laminate: '',
    sb_taping: '',
    sb_rounded_corners: 'none',
    sb_rounded_corners_custom: '',
    sb_mounting_holes: false,
    sb_hole_location: 'all-4-corners',
    sb_hole_diameter: 'match-hardware',
    sb_hardware_needed: false,
    sb_hardware_desc: '',
    sb_grommets: '',
    sb_hem: '',
    sb_notes: '',
    std_custom_enabled: false,
    std_custom_fields: [],
    discontinue_reason: '',
    discontinue_effective: '',
    parts_by_product: {},
    parts_touched: {},
    artwork_colors: false,
    artwork_logos: false,
    artwork_fonts: false,
    artwork_layout: false,
    artwork_notes: '',
    product_linked: {},
    product_overrides: {},
  }
}

/** F-131 — an empty change set for a product that edits its own changes ("Clear changes"). */
export function createEmptyModifyProductChanges(): ModifyProductChanges {
  return createDefaultModifyFormData()
}

export function createEmptyModifySnapshot(): ModifyWizardSnapshot {
  return {
    step: 1,
    selectedProducts: [],
    formData: createDefaultModifyFormData(),
    expanded: [],
    changeMode: null,
  }
}

export function createEmptySignBuilderSnapshot(): SignBuilderWizardSnapshot {
  return {
    formData: createDefaultSignBuilderFormData(),
    openSections: [1],
    activeSection: 1,
    visitedSections: [1],
    showSummary: false,
    decisionAnswers: {},
    builtProducts: [],
    editingProductIdx: null,
    step: 1,
    client: '',
    projectDescription: '',
  }
}

/**
 * Merge persisted Sign Builder draft fields with current defaults (backward compat).
 * A2: sole hydrate for checkbox-era holes → location (NP / NCO `_sb` / Bundle Edit spec).
 */
/**
 * DD-NP-QTY-001 — quantity as typed in the form ('' / '25' / '1.5'); drafts and cart snapshots
 * from before the field existed (or a non-string) come back as the prefilled '1'.
 */
export function normalizeSignBuilderQuantityInput(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value) // 1.5 stays 1.5 so validation can name it, never a silent 1
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim()
  }
  return '1'
}

/** F-063 — the minimum choice as stored; anything else (older drafts, junk) reads as none. */
export function normalizeSignBuilderMinimumQuantityChoice(value: unknown): SignBuilderMinimumQuantity {
  return value === '25' || value === '50' || value === '100' || value === 'custom' ? value : ''
}

export function normalizeSignBuilderFormData(
  data: Partial<SignBuilderFormData>
): SignBuilderFormData {
  const defaults = createDefaultSignBuilderFormData()
  const merged: SignBuilderFormData = {
    ...defaults,
    ...data,
    sb_white_ink: {
      ...defaults.sb_white_ink,
      ...(typeof data.sb_white_ink === 'object' && data.sb_white_ink !== null
        ? data.sb_white_ink
        : {}),
    },
    sb_reference_sku:
      data.sb_reference_sku && typeof data.sb_reference_sku === 'object'
        ? data.sb_reference_sku
        : null,
    sb_reference_sku_enabled:
      typeof data.sb_reference_sku_enabled === 'boolean'
        ? data.sb_reference_sku_enabled
        : Boolean(data.sb_reference_sku),
    known_uin_enabled:
      typeof data.known_uin_enabled === 'boolean'
        ? data.known_uin_enabled
        : Boolean(data.known_uin?.trim()),
    is_standard_custom: Boolean(data.is_standard_custom),
    custom_fields: normalizeStandardCustomFields(data.custom_fields),
  }

  merged.quantity = normalizeSignBuilderQuantityInput(data.quantity)
  merged.minimum_quantity = normalizeSignBuilderMinimumQuantityChoice(data.minimum_quantity)
  merged.minimum_quantity_custom =
    typeof data.minimum_quantity_custom === 'string' ? data.minimum_quantity_custom.trim() : ''
  merged.build_mode = data.build_mode === 'describe' ? 'describe' : 'spec'
  merged.build_description =
    typeof data.build_description === 'string' ? data.build_description : ''
  merged.extra_sections = normalizeExtraSections(data.extra_sections)
  merged.primary_section_label =
    typeof data.primary_section_label === 'string' ? data.primary_section_label : ''
  merged.primary_section_pieces =
    typeof data.primary_section_pieces === 'string' && data.primary_section_pieces.trim()
      ? data.primary_section_pieces
      : '1'
  merged.primary_vended = data.primary_vended === true

  let holeLocation = merged.sb_hole_location?.trim() || ''
  if (data.sb_mounting_holes === true) {
    holeLocation = data.sb_hole_location?.trim() || 'all-4-corners'
  } else if (data.sb_mounting_holes === false) {
    holeLocation = 'none'
  } else if (!holeLocation) {
    holeLocation = defaults.sb_hole_location
  }

  merged.sb_hole_location = holeLocation
  merged.sb_mounting_holes = holeLocation !== 'none'
  return merged
}

/** F-131 — a product's own change set from a draft, every field normalised like the main form (never nested). */
export function normalizeModifyProductChanges(raw: unknown): ModifyProductChanges {
  const data = raw && typeof raw === 'object' ? (raw as Partial<ModifyFormData>) : {}
  return normalizeModifyFormData({ ...data, why: '', product_linked: {}, product_overrides: {} })
}

/** `product_linked` keeps `false` (the product edits its own changes); anything else means linked. */
function normalizeLinked(raw: unknown): Record<string, boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, boolean> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === false) out[key] = false
  }
  return out
}

function normalizeProductOverrides(raw: unknown): Record<string, ModifyProductChanges> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: Record<string, ModifyProductChanges> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value && typeof value === 'object') out[key] = normalizeModifyProductChanges(value)
  }
  return out
}

/** Merge persisted draft fields with current defaults (backward compat). */
export function normalizeModifyFormData(data: Partial<ModifyFormData>): ModifyFormData {
  const defaults = createDefaultModifyFormData()
  return {
    ...defaults,
    ...data,
    sb_white_ink: {
      ...defaults.sb_white_ink,
      ...(typeof data.sb_white_ink === 'object' && data.sb_white_ink !== null
        ? data.sb_white_ink
        : {}),
    },
    sb_reference_sku:
      data.sb_reference_sku && typeof data.sb_reference_sku === 'object'
        ? (data.sb_reference_sku as ModifyReferenceSku)
        : null,
    sb_reference_sku_enabled:
      typeof data.sb_reference_sku_enabled === 'boolean'
        ? data.sb_reference_sku_enabled
        : Boolean(data.sb_reference_sku),
    std_custom_enabled: Boolean(data.std_custom_enabled),
    std_custom_fields: normalizeStandardCustomFields(data.std_custom_fields),
    // F-099 slice 7 — drafts saved before the Parts / Artwork Update categories open with none.
    parts_by_product: normalizeModifyPartsByProduct(data.parts_by_product),
    parts_touched: normalizeTouched(data.parts_touched),
    artwork_colors: data.artwork_colors === true,
    artwork_logos: data.artwork_logos === true,
    artwork_fonts: data.artwork_fonts === true,
    artwork_layout: data.artwork_layout === true,
    artwork_notes: typeof data.artwork_notes === 'string' ? data.artwork_notes : '',
    // F-131 — drafts saved before per-product changes open with every product linked.
    product_linked: normalizeLinked(data.product_linked),
    product_overrides: normalizeProductOverrides(data.product_overrides),
  }
}

export type SignBuilderFormat = 'wide' | 'small' | null

/** F-063 — the Minimum quantity choice on the New Product form ('' = none). */
export type SignBuilderMinimumQuantity = '' | '25' | '50' | '100' | 'custom'
/** F-063 — Candace's main minimums; the D&D attribute offers the same list (+ "Other…"). */
export const SIGN_BUILDER_MINIMUM_QUANTITY_PRESETS: readonly string[] = ['25', '50', '100']

/** Tri-state paneling: true = paneled, false = single sheet, internal = defer to PD. */
export type SignBuilderPaneling = true | false | 'internal' | null

/** Section-2 build fork — spec the substrate yourself, or describe the idea for D&D. */
export type SignBuilderBuildMode = 'spec' | 'describe'

/** Extra stock section beyond the primary §2 fields (DD-MULTIPART-002 / Origin extraSections). */
export interface SignBuilderExtraSectionDraft {
  label: string
  substrate: string
  width: string
  height: string
  pieces: string
  single_double: string
  notes: string
  /**
   * F-099 / F-120 — Sales marks a vended (drop-shipped) part; substrate and size are not asked
   * for it (Candace: "we do not list dimensions for vended products"). D&D fills the supplier
   * info in Triage — Sales only sees "vended" (Candace #11).
   */
  vended: boolean
}

export function createEmptyExtraSectionDraft(): SignBuilderExtraSectionDraft {
  return {
    label: '',
    substrate: '',
    width: '',
    height: '',
    pieces: '1',
    single_double: 'single',
    notes: '',
    vended: false,
  }
}

function normalizeExtraSections(
  raw: unknown
): SignBuilderExtraSectionDraft[] {
  if (!Array.isArray(raw)) return []
  return raw.map((entry) => {
    const s =
      entry && typeof entry === 'object'
        ? (entry as Partial<SignBuilderExtraSectionDraft>)
        : {}
    return {
      label: typeof s.label === 'string' ? s.label : '',
      substrate: typeof s.substrate === 'string' ? s.substrate : '',
      width: typeof s.width === 'string' ? s.width : '',
      height: typeof s.height === 'string' ? s.height : '',
      pieces: typeof s.pieces === 'string' && s.pieces.trim() ? s.pieces : '1',
      single_double:
        typeof s.single_double === 'string' && s.single_double.trim()
          ? s.single_double
          : 'single',
      notes: typeof s.notes === 'string' ? s.notes : '',
      vended: s.vended === true,
    }
  })
}

/** Local wizard form state for Sign Builder (ID-canonical catalog selections). */
export interface SignBuilderFormData {
  product_description: string
  /**
   * DD-NP-QTY-001 (F-079) — pieces Sales is quoting for this product; required, whole number ≥ 1,
   * prefilled '1'. Wire: spec.quantity (was hardcoded 1 since 406c2b896).
   */
  quantity: string
  /**
   * F-063 / F-098 / F-102 (Candace, Marguerite) — the product's order minimum: one of the
   * main minimums (25 / 50 / 100), 'custom' with the number typed below, or '' = none.
   * Optional on every product (Marguerite 2026-10-08: mostly small format, also small vinyl,
   * magnets, vended items). Wire: spec.minimum_quantity, only when set.
   */
  minimum_quantity: SignBuilderMinimumQuantity
  /** The number typed when `minimum_quantity === 'custom'` (a whole number ≥ 1). */
  minimum_quantity_custom: string
  format: SignBuilderFormat
  category: string
  known_uin_enabled: boolean
  known_uin: string
  /** Spec vs describe-the-idea (DD-DESCRIBE-001a). Default `'spec'`. */
  build_mode: SignBuilderBuildMode
  /** Required when `build_mode === 'describe'`. */
  build_description: string
  /** Multi-part extras beyond primary §2 (DD-MULTIPART-002). */
  extra_sections: SignBuilderExtraSectionDraft[]
  /** Shown when extras exist — wire sections[0].label (default Main). */
  primary_section_label: string
  /** Shown when extras exist — wire sections[0].pieces. */
  primary_section_pieces: string
  /** F-099 / F-120 — the main build is a vended (drop-shipped) item: no substrate / size asked. */
  primary_vended: boolean
  ada_color_spec: string
  ada_pictogram: string
  ada_tactile_text: string
  ada_braille: string
  ada_build_notes: string
  paneling: SignBuilderPaneling
  panel_count: string
  panel_width: string
  panel_height: string
  panel_notes: string
  sb_pole_pocket: boolean
  sb_hole_diameter_custom: string
  sb_substrate: string
  sb_width: string
  sb_height: string
  sb_single_double: string
  sb_graphic_method: string
  sb_vinyl_type: string
  sb_rta_color: string
  sb_white_ink: ModifyWhiteInk
  sb_reference_sku_enabled: boolean
  sb_reference_sku: ModifyReferenceSku | null
  sb_design_notes: string
  sb_laminate: string
  sb_taping: string
  sb_rounded_corners: string
  sb_rounded_corners_custom: string
  sb_mounting_holes: boolean
  sb_hole_location: string
  sb_hole_diameter: string
  sb_hardware_needed: boolean
  sb_hardware_desc: string
  sb_grommets: string
  sb_hem: string
  sb_notes: string
  /** Standard-Custom authoring (NP overlay only via allowStandardCustom). */
  is_standard_custom: boolean
  custom_fields: StandardCustomFieldDraft[]
}

export function createDefaultSignBuilderFormData(): SignBuilderFormData {
  const modifyDefaults = createDefaultModifyFormData()
  return {
    product_description: '',
    quantity: '1',
    minimum_quantity: '',
    minimum_quantity_custom: '',
    format: null,
    category: '',
    known_uin_enabled: false,
    known_uin: '',
    build_mode: 'spec',
    build_description: '',
    extra_sections: [],
    primary_section_label: '',
    primary_section_pieces: '1',
    primary_vended: false,
    ada_color_spec: '',
    ada_pictogram: '',
    ada_tactile_text: '',
    ada_braille: '',
    ada_build_notes: '',
    paneling: null,
    panel_count: '',
    panel_width: '',
    panel_height: '',
    panel_notes: '',
    sb_pole_pocket: false,
    sb_hole_diameter_custom: '',
    sb_substrate: modifyDefaults.sb_substrate,
    sb_width: modifyDefaults.sb_width,
    sb_height: modifyDefaults.sb_height,
    sb_single_double: modifyDefaults.sb_single_double,
    sb_graphic_method: 'none',
    sb_vinyl_type: modifyDefaults.sb_vinyl_type,
    sb_rta_color: modifyDefaults.sb_rta_color,
    sb_white_ink: { ...modifyDefaults.sb_white_ink },
    sb_reference_sku_enabled: modifyDefaults.sb_reference_sku_enabled,
    sb_reference_sku: modifyDefaults.sb_reference_sku,
    sb_design_notes: modifyDefaults.sb_design_notes,
    sb_laminate: 'none',
    sb_taping: modifyDefaults.sb_taping,
    sb_rounded_corners: modifyDefaults.sb_rounded_corners,
    sb_rounded_corners_custom: modifyDefaults.sb_rounded_corners_custom,
    sb_mounting_holes: false,
    sb_hole_location: 'none',
    sb_hole_diameter: modifyDefaults.sb_hole_diameter,
    sb_hardware_needed: modifyDefaults.sb_hardware_needed,
    sb_hardware_desc: modifyDefaults.sb_hardware_desc,
    sb_grommets: modifyDefaults.sb_grommets,
    sb_hem: modifyDefaults.sb_hem,
    sb_notes: modifyDefaults.sb_notes,
    is_standard_custom: false,
    custom_fields: [],
  }
}

/** Sign Builder cart line — wire { spec, sop_warnings } plus lossless edit snapshot (SB-OUT-001). */
export interface SignBuilderCartEntry {
  spec: OriginSpec
  sop_warnings: OriginSopWarning[]
  formData: SignBuilderFormData
  attachments: ModifyAttachment[]
}

/** Cart line shape stored in drafts — attachments are session-only. */
export type SignBuilderPersistedCartEntry = Pick<
  SignBuilderCartEntry,
  'spec' | 'sop_warnings' | 'formData'
>

export function toPersistedCartEntries(
  entries: SignBuilderCartEntry[]
): SignBuilderPersistedCartEntry[] {
  return entries.map(({ spec, sop_warnings, formData }) => ({
    spec,
    sop_warnings,
    formData: normalizeSignBuilderFormData(formData),
  }))
}

export function fromPersistedCartEntries(
  entries: SignBuilderPersistedCartEntry[]
): SignBuilderCartEntry[] {
  return entries.map(({ spec, sop_warnings, formData }) => ({
    spec,
    sop_warnings,
    formData: normalizeSignBuilderFormData(formData),
    attachments: [],
  }))
}
